/**
 * 연속 스크롤이 요구한 조회 구간을 «넓히는» 것 (C-2b · 2026-09-29).
 *
 * ## ★ 왜 «합치기»가 아니라 «넓히기»인가
 *
 * 정본은 `loaded` 범위를 들고 «델타만» 가져와 기존 목록에 **합쳤다**. 그 길에는 중복이 따라온다
 * — 같은 일정이 두 구간에 걸치면 두 벌이 되고, `occurrenceKey` 로 또 걸러야 한다.
 *
 * 여기서는 **구간만 넓히고** 조회는 기존 `refresh()` 가 그 한 구간을 통째로 한다. 한 번에 한
 * 요청이므로 **합칠 것이 없고 중복이 «원리상» 안 생긴다.**
 *   · 요청 «수»는 같다 — `useCoalescedRange` 가 스크롤이 멈출 때 한 번만 보낸다
 *   · 대가는 «바이트» — 델타(6주)가 아니라 넓힌 구간(최대 210일)을 다시 받는다
 *
 * ## ⭐ 「같으면 같은 것을 돌려준다」가 요청 하나를 아낀다
 *
 * 이미 덮은 구간을 또 요구하면(가장자리를 오갈 때 실제로 그렇다) **`prev` 를 «그대로»** 돌려준다.
 * 그러면 React 가 상태를 안 바꾼 것으로 보아 리렌더가 없고, 조회 effect 도 다시 안 돈다.
 * ⛔ 새 객체를 만들어 돌려주면 «같은 구간을 다시 받는다» — 값은 같아도 참조가 달라서다.
 *
 * ## ⛔ 상한은 «여기서» 봐야 한다 — `useCoalescedRange` 의 상한이 이것을 못 막는다
 *
 * 처음에 「상한은 `useCoalescedRange` 가 잘라 주므로 여기서 안 본다」고 적었는데 **틀렸다.**
 * 그쪽 `clampToward` 가 자르는 것은 **«한 요청»의 길이**다. 여기서 쌓이는 것은 **누적 합집합**
 * 이라, 한 요청이 늘 6주여도 **한 방향으로 오래 굴리면 합집합이 혼자 자란다** — 200일쯤
 * 굴러가면 합집합은 291일이 되고, 더 가면 **서버 상한 365일(`calendar-helpers.ts:24`
 * `MAX_RANGE_MS`)을 넘어 400** 이 된다. 화면(DOM)은 24주(168일)만 들고 있으니 굴러간 만큼을
 * 계속 들고 있을 까닭도 없다.
 *
 * ⇒ 그래서 `useCoalescedRange` 와 **같은 상한·같은 방식**으로 여기서도 자른다 —
 *   **마지막 요구가 있는 쪽 끝**을 남기고 먼 쪽을 버린다(보이는 창은 언제나 덮인다).
 */

/** 24주(DOM 창) + 6주(확장 한 덩어리). `useCoalescedRange.MAX_SPAN_DAYS` 와 같은 값·같은 까닭. */
export const MAX_UNION_SPAN_DAYS = 210;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface IsoRange {
  /** ISO 8601 (UTC). 문자열 비교가 곧 시각 비교라 그대로 크기를 견줄 수 있다. */
  from: string;
  to: string;
}

/**
 * `prev` 에 `[from, to)` 를 더해 넓힌 구간. 이미 덮여 있으면 **`prev` 를 그대로** 돌려준다.
 *
 * ⚠ `prev` 가 `null` 이면(아직 아무것도 요구되지 않음) 새 구간을 그대로 쓴다.
 * ⚠ 넓힌 결과가 210일을 넘으면 **마지막 요구 쪽을 남기고** 반대쪽을 버린다(위 ⛔ 절 참조).
 */
export function unionIsoRange(prev: IsoRange | null, from: Date, to: Date): IsoRange {
  const f = from.toISOString();
  const t = to.toISOString();
  if (!prev) return { from: f, to: t };
  const nextFrom = prev.from < f ? prev.from : f;
  const nextTo = prev.to > t ? prev.to : t;
  // ★ 이미 덮었다 — «같은 객체»를 돌려준다(다시 받지 않게).
  if (nextFrom === prev.from && nextTo === prev.to) return prev;

  const maxSpanMs = MAX_UNION_SPAN_DAYS * DAY_MS;
  if (Date.parse(nextTo) - Date.parse(nextFrom) > maxSpanMs) {
    // 방금 요구가 «미래 끝»을 밀었나 — 그러면 그 끝을 남기고 과거를 버린다(아니면 반대).
    const movingFuture = t >= nextTo;
    return movingFuture
      ? { from: new Date(Date.parse(nextTo) - maxSpanMs).toISOString(), to: nextTo }
      : { from: nextFrom, to: new Date(Date.parse(nextFrom) + maxSpanMs).toISOString() };
  }
  return { from: nextFrom, to: nextTo };
}
